import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { getNavForRole, FilteredViewCategory } from '../config/navConfig';
import { useAuth } from '../contexts/AuthContext';

interface UseFocusedNavResult {
  navViews: FilteredViewCategory[];
  focusedModeActive: boolean;
  escapeHatchActive: boolean;
  toggleEscapeHatch: () => void;
  loading: boolean;
  activeGroupNames: string[];
}

const ESCAPE_HATCH_KEY = 'kdu-focused-nav-escape-hatch';

/**
 * Hook that resolves the focused navigation set for the current user.
 * Calls GET /focused-nav/resolve on mount and when escape hatch changes.
 * Falls back to standard role-based nav if the API call fails.
 */
export function useFocusedNav(): UseFocusedNavResult {
  const { user } = useAuth();
  const [navViews, setNavViews] = useState<FilteredViewCategory[]>([]);
  const [focusedModeActive, setFocusedModeActive] = useState(false);
  const [activeGroupNames, setActiveGroupNames] = useState<string[]>([]);
  const [escapeHatchActive, setEscapeHatchActive] = useState(() => {
    return sessionStorage.getItem(ESCAPE_HATCH_KEY) === 'true';
  });
  const [loading, setLoading] = useState(true);

  const fetchNav = useCallback(async () => {
    try {
      const data = await apiClient<{
        focusedModeActive: boolean;
        navigation: FilteredViewCategory[];
        escapeHatchAvailable: boolean;
        activeGroupNames: string[];
      }>(`/focused-nav/resolve?escapeHatch=${escapeHatchActive}`);

      setNavViews(data.navigation);
      setFocusedModeActive(data.focusedModeActive);
      setActiveGroupNames(data.activeGroupNames || []);
    } catch {
      // Fallback to standard role-based nav on API failure
      if (user?.role) {
        setNavViews(getNavForRole(user.role));
      }
      setFocusedModeActive(false);
    } finally {
      setLoading(false);
    }
  }, [escapeHatchActive, user?.role]);

  useEffect(() => {
    fetchNav();
  }, [fetchNav]);

  const toggleEscapeHatch = useCallback(() => {
    const newValue = !escapeHatchActive;
    setEscapeHatchActive(newValue);
    sessionStorage.setItem(ESCAPE_HATCH_KEY, String(newValue));
  }, [escapeHatchActive]);

  return {
    navViews,
    focusedModeActive,
    escapeHatchActive,
    toggleEscapeHatch,
    loading,
    activeGroupNames,
  };
}
